// =====================================================================
// src/modulos/auditoria.js — os indicadores da operação e a trilha de um
// apartamento
//
// DUAS ABAS (Hugo, 24/09/2026): "Indicadores" abre primeiro — tempo médio
// de liberação do quarto (check-out → início da limpeza → limpeza →
// liberação), de resposta aos chamados e de check-in no balcão, com e sem
// pré-check-in. O padrão é o DIA de hoje (do hotel); o período se escolhe
// em De/Até ou nos atalhos. Os números vêm prontos do banco (0060,
// `indicadores_operacionais`); os cartões copiam o `_gtCartao` da Gestão
// de Tempo do MdM (index.html 20185).
//
// A pergunta da ata de 11/09: "o que aconteceu no 1104?". O banco responde
// desde a 0026 (`trilha_do_apartamento`); esta é a tela que pergunta.
//
// UMA PERGUNTA POR VEZ: um apartamento, um período. Sem apartamento
// escolhido a tela não busca nada — "me dê tudo" não é a pergunta que
// alguém faz numa auditoria, e um dump de milhares de linhas não responde
// nada.
//
// O que NÃO está aqui, e é de propósito:
//   - valor de reserva. A 0026 filtra `reserva.valor` no banco, para todo
//     mundo (INVARIANTE 2). A tela nem sabe que existe.
//   - cartão de acesso e log de porta. Dependem do SAGA e das fechaduras,
//     que são integração sob orçamento. Melhor a tela não prometer.
//   - busca por hóspede ("onde a dona Tatiana esteve?"). É outra pergunta
//     e outra tela; decidido em 16/09 que fica para depois.
// =====================================================================
import { hojeDe } from '../lib/contexto.js';
import { el, icone, aviso, barraDeAbas } from '../ui.js';
import * as In from '../lib/indicadores.js';
import {
  porDia, rotuloDaAcao, grupoDaAcao, detalheDoEvento, quemDoEvento, horaDe,
  resumoDaTrilha, periodoPadrao,
} from '../lib/auditoria.js';
import { paraPdf, paraPlanilha, temPdf, temPlanilha, nomeDoArquivo } from '../lib/exportar.js';

const ATALHOS = [[7, '7 dias'], [30, '30 dias'], [90, '90 dias']];
const ATALHOS_IND = [[1, 'Hoje'], [7, '7 dias'], [30, '30 dias'], [90, '90 dias']];

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  // sem `podeEditar`: auditoria é só leitura. Não há botão que escreva
  // nesta tela, e é por isso que o nível `editar` não muda nada aqui.
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  const fuso = (contexto.cliente && contexto.cliente.fuso) || 'UTC';
  let vivo = true;
  let unidades = [], trilha = null, carregando = false;
  const hoje = () => hojeDe(contexto, agora());
  let { de, ate } = periodoPadrao(hoje(), 30);
  let dias = 30;
  let numero = '';
  // A imobiliária (Monteiro) não lê a operação do prédio:
  // o banco recusa (0060), e a tela nem oferece a aba — ela abre direto na
  // trilha, que é a pergunta dela.
  const veIndicadores = !contexto.imobiliaria;   // 0099: toda imobiliária é imobiliária
  let aba = veIndicadores ? 'indicadores' : 'trilha';
  // os indicadores: o DIA de hoje por padrão (pedido do Hugo)
  let iDe = hoje(), iAte = hoje(), iDias = 1;
  let indicadores = null, carregandoInd = false, pedidoInd = 0;
  const graficos = [];
  // O Administrador do Sistema não tem cliente (`contexto.cliente` é NULO,
  // CLAUDE.md §3.2): escolhe o hotel num seletor, como em checkin.js. Ler
  // `contexto.cliente.id` aqui dava "Cannot read properties of null" na
  // primeira busca (20/09).
  let clienteSel = sistema ? null : contexto.cliente.id;
  let clientes = [];
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});

  async function carregarUnidades() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (sistema && !clienteSel) { unidades = []; return; }
    const corte = sistema ? `&cliente_id=eq.${clienteSel}` : '';
    unidades = await auth.ler('unidades', `select=numero,andar&ativo=eq.true${corte}&order=numero`);
  }
  async function trocarCliente(id) {
    clienteSel = id; numero = ''; trilha = null; indicadores = null;
    try { await carregarUnidades(); } catch (e) { aviso(e.message, 'erro'); return; }
    if (aba === 'indicadores') buscarIndicadores(); else desenhar();
  }

  // ── os indicadores ─────────────────────────────────────────────────
  async function buscarIndicadores() {
    if (sistema && !clienteSel) { indicadores = []; desenhar(); return; }
    const meu = ++pedidoInd;   // troca rápida de período: vale a última resposta
    carregandoInd = true; desenhar();
    let r;
    try {
      r = await auth.rpc('indicadores_operacionais', { p_de: iDe, p_ate: iAte, ...pc() });
    } catch (e) { if (meu === pedidoInd) { carregandoInd = false; desenhar(); aviso(e.message, 'erro'); } return; }
    if (meu !== pedidoInd) return;
    indicadores = r || []; carregandoInd = false;
    desenhar();
  }
  function atalhoInd(n) {
    iDias = n;
    const p = periodoPadrao(hoje(), n);
    iDe = p.de; iAte = p.ate;
    buscarIndicadores();
  }
  function trocarAba(chave) {
    aba = chave;
    if (aba === 'indicadores' && indicadores === null) buscarIndicadores(); else desenhar();
  }

  async function buscar() {
    if (!numero) { trilha = null; desenhar(); return; }
    carregando = true; desenhar();
    try {
      trilha = await auth.rpc('trilha_do_apartamento', { p_numero: numero, p_de: de, p_ate: ate, ...pc() });
    } catch (e) { carregando = false; aviso(e.message, 'erro'); return; }
    carregando = false;
    desenhar();
  }

  function usarAtalho(n) {
    dias = n;
    const p = periodoPadrao(hoje(), n);
    de = p.de; ate = p.ate;
    if (numero) buscar(); else desenhar();
  }

  // ── exportar ───────────────────────────────────────────────────────
  // PDF e planilha pelo mesmo caminho do app do Monteiro de Moraes:
  // jsPDF + autoTable e SheetJS, servidas de /vendor/. As colunas são as
  // mesmas nos dois — quem manda o PDF e a planilha juntos não deve achar
  // que são coisas diferentes.
  const COLUNAS = ['Data', 'Hora', 'O que aconteceu', 'Detalhe', 'Quem'];
  const brasileira = iso => iso.split('-').reverse().join('/');
  function linhasParaExportar() {
    const saida = [];
    for (const g of porDia(trilha, { fuso, hoje: hoje() }))
      for (const e of g.eventos)
        saida.push([brasileira(g.dia), horaDe(e.em, fuso), rotuloDaAcao(e.acao), detalheDoEvento(e), quemDoEvento(e).nome]);
    return saida;
  }
  // No PDF e na planilha, o hotel é o escolhido no seletor quando quem
  // exporta é o sistema — não "Hotel" genérico.
  const clienteAtual = () => (sistema ? clientes.find(c => c.id === clienteSel) : contexto.cliente) || null;
  const hotel = () => (clienteAtual() && clienteAtual().nome) || 'Hotel';
  const slug = () => (clienteAtual() && clienteAtual().slug) || 'hotel';

  function baixarPdf() {
    try {
      const linhas = linhasParaExportar();
      paraPdf({
        titulo: `Auditoria do apartamento ${numero}`, hotel: hotel(),
        subtitulo: `${brasileira(de)} a ${brasileira(ate)} · ${linhas.length} registro(s) · gerado em ${new Date().toLocaleDateString('pt-BR')}`,
        colunas: COLUNAS, linhas,
        arquivo: nomeDoArquivo('auditoria', [slug(), numero, de, ate], 'pdf'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }
  function baixarPlanilha() {
    try {
      paraPlanilha({
        aba: `Apto ${numero}`, colunas: COLUNAS, linhas: linhasParaExportar(),
        larguras: [12, 8, 30, 46, 26],
        arquivo: nomeDoArquivo('auditoria', [slug(), numero, de, ate], 'xlsx'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }

  // ── desenho ────────────────────────────────────────────────────────
  function desenhar() {
    if (!vivo) return;
    destruirGraficos();
    cont.replaceChildren();
    cont.dataset.tela = 'auditoria';
    if (veIndicadores) cont.appendChild(barraDeAbas('ad-abas', [
      { chave: 'indicadores', rotulo: 'Indicadores' },
      { chave: 'trilha', rotulo: 'Trilha do apartamento' },
    ], aba, trocarAba));
    if (aba === 'indicadores') { desenharIndicadores(); return; }
    cont.appendChild(filtros());
    if (carregando) { cont.appendChild(el('p', { class: 'vazio', id: 'ad-carregando' }, 'Procurando…')); return; }
    if (trilha === null) {
      cont.appendChild(el('p', { class: 'vazio', id: 'ad-sem-apto' },
        'Escolha um apartamento e o período. A trilha mostra reservas, hóspedes, check-in, check-out, pré-check-in e limpeza — nunca valor de reserva.'));
      return;
    }
    cont.appendChild(el('div', { class: 'secao-titulo', id: 'ad-cabeca' },
      `Apartamento ${numero}`,
      el('span', { class: 'usu-email', style: { marginLeft: '10px', fontWeight: '400' }, id: 'ad-resumo' }, resumoDaTrilha(trilha))));
    if (!trilha.length) {
      cont.appendChild(el('p', { class: 'vazio', id: 'ad-vazio' },
        'Nada aconteceu neste apartamento no período. Se você esperava encontrar algo, confira o período — ou o apartamento pode ser de outra imobiliária.'));
      return;
    }
    const lista = el('div', { id: 'ad-trilha' });
    for (const g of porDia(trilha, { fuso, hoje: hoje() })) {
      lista.appendChild(el('div', { class: 'secao-titulo', style: { marginTop: '16px', fontSize: '13px' }, dataset: { dia: g.dia } }, g.rotulo));
      // `table-cards` é do MdM: no desktop é tabela, no celular vira
      // cartão sozinha, usando o data-label de cada célula.
      lista.appendChild(el('div', { class: 'card', style: { padding: '0' }, dataset: { diaCartao: g.dia } },
        el('table', { class: 'table-cards' },
          el('thead', null, el('tr', null, el('th', null, 'Hora'), el('th', null, 'O que aconteceu'), el('th', null, 'Quem'))),
          el('tbody', null, g.eventos.map(linha)))));
    }
    cont.appendChild(lista);
  }

  const seletorDeHotel = id => (sistema ? el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: id }, 'Hotel'),
    el('select', { class: 'input', id, style: { width: 'auto' }, onchange: ev => trocarCliente(ev.target.value) },
      clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome)))) : null);

  function destruirGraficos() {
    for (const g of graficos) { try { g.destroy(); } catch (_) { /* já destruído */ } }
    graficos.length = 0;
  }

  function filtrosInd() {
    // recusado, os campos voltam ao que a tela mostra (como na barra do
    // quadro): campo dizendo uma coisa e gráfico mostrando outra engana
    const aplicar = (qual, v) => {
      const de = qual === 'de' ? v : iDe, ate = qual === 'ate' ? v : iAte;
      if (!de || !ate || ate < de) { aviso('Período inválido: o "Até" não pode vir antes do "De".', 'erro'); desenhar(); return; }
      iDe = de; iAte = ate; iDias = 0; buscarIndicadores();
    };
    return el('div', { class: 'usu-filtros', id: 'ind-filtros' },
      seletorDeHotel('ind-cliente'),
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'ind-de' }, 'De'),
        el('input', { type: 'date', class: 'input', id: 'ind-de', value: iDe, max: iAte, onchange: ev => aplicar('de', ev.target.value) })),
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'ind-ate' }, 'Até'),
        el('input', { type: 'date', class: 'input', id: 'ind-ate', value: iAte, min: iDe, onchange: ev => aplicar('ate', ev.target.value) })),
      el('div', { class: 'tabs-row', style: { marginBottom: '0', alignItems: 'flex-end' }, id: 'ind-atalhos' },
        ATALHOS_IND.map(([n, r]) => el('button', { type: 'button', class: 'btn btn-sm ' + (iDias === n ? 'btn-primary' : 'btn-secondary'), dataset: { atalho: String(n) }, onclick: () => atalhoInd(n) }, r))));
  }

  // `_gtCartao` do MdM (20185): rótulo pequeno cinza, número grande na cor
  function cartao(k, r) {
    const m = In.METRICAS[k];
    return el('div', { dataset: { metrica: k }, style: { flex: '1', minWidth: '150px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 12px' } },
      el('div', { style: { fontSize: '11px', color: '#6b7280', fontWeight: '600' } }, m.rotulo),
      el('div', { dataset: { valor: '1' }, style: { fontSize: '20px', fontWeight: '700', color: r.n ? m.cor : '#9ca3af', fontVariantNumeric: 'tabular-nums' } }, In.fmtMin(r.media)),
      el('div', { dataset: { n: String(r.n) }, style: { fontSize: '11px', color: '#9ca3af' } }, In.rotuloDoN(r.n)));
  }

  function desenharIndicadores() {
    cont.appendChild(filtrosInd());
    const titulo = In.umDia(iDe, iAte)
      ? (iDe === hoje() ? 'Hoje' : brasileira(iDe))
      : `${brasileira(iDe)} a ${brasileira(iAte)}`;
    cont.appendChild(el('div', { class: 'secao-titulo', id: 'ind-periodo' }, titulo,
      el('span', { class: 'usu-email', style: { marginLeft: '10px', fontWeight: '400' } },
        In.umDia(iDe, iAte) ? 'médias do dia' : 'médias do período · gráfico dia a dia')));
    if (carregandoInd || indicadores === null) { cont.appendChild(el('p', { class: 'vazio', id: 'ind-carregando' }, 'Calculando…')); return; }
    const r = In.resumo(indicadores);
    const grade = el('div', { id: 'ind-grade', style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' } });
    for (const g of In.GRUPOS) {
      const semDado = g.metricas.every(k => !r[k].n);
      grade.appendChild(el('div', { class: 'card', dataset: { grupo: g.chave }, style: g.chave === 'quarto' ? { gridColumn: '1/-1' } : {} },
        el('h3', { style: { fontSize: '14px', fontWeight: '700', color: '#374151', margin: '0 0 2px' } }, g.titulo),
        el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } }, g.explica),
        el('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' } }, g.metricas.map(k => cartao(k, r[k]))),
        semDado
          ? el('p', { class: 'vazio', dataset: { semDado: '1' }, style: { margin: '0' } }, 'Nenhum registro concluído no período.')
          : el('div', { style: { height: '220px' } }, el('canvas', { id: 'ind-graf-' + g.chave,
            dataset: { tipo: In.umDia(iDe, iAte) ? 'barra' : 'linha', serie: JSON.stringify(g.metricas.map(k => In.umDia(iDe, iAte) ? r[k].media : In.seriePorDia(indicadores, iDe, iAte, k))) } }))));
    }
    cont.appendChild(grade);
    // o Chart.js entra por <script defer>, como no Financeiro: sem ele o
    // canvas fica em branco, e a série segue legível no data-serie
    setTimeout(desenharGraficos, 0);
  }
  function desenharGraficos() {
    destruirGraficos();
    const Chart = globalThis.Chart;
    if (!Chart || !vivo || aba !== 'indicadores' || !indicadores) return;
    for (const g of In.GRUPOS) {
      const c = document.getElementById('ind-graf-' + g.chave);
      if (c) graficos.push(new Chart(c, In.configDoGrafico(g, indicadores, iDe, iAte)));
    }
  }

  function filtros() {
    const sel = el('select', { class: 'input', id: 'ad-apto', style: { width: 'auto' }, onchange: ev => { numero = ev.target.value; buscar(); } },
      el('option', { value: '' }, 'Escolha o apartamento'),
      unidades.map(u => el('option', { value: u.numero, selected: u.numero === numero }, u.numero)));
    return el('div', { class: 'usu-filtros', id: 'ad-filtros' },
      seletorDeHotel('ad-cliente'),
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'ad-apto' }, 'Apartamento'), sel),
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'ad-de' }, 'De'),
        el('input', { type: 'date', class: 'input', id: 'ad-de', value: de, max: ate, onchange: ev => { de = ev.target.value; dias = 0; if (numero) buscar(); } })),
      el('div', { class: 'form-row', style: { margin: 0 } }, el('label', { class: 'label', for: 'ad-ate' }, 'Até'),
        el('input', { type: 'date', class: 'input', id: 'ad-ate', value: ate, min: de, onchange: ev => { ate = ev.target.value; dias = 0; if (numero) buscar(); } })),
      el('div', { class: 'tabs-row', style: { marginBottom: '0', alignItems: 'flex-end' }, id: 'ad-atalhos' },
        ATALHOS.map(([n, r]) => el('button', { type: 'button', class: 'btn btn-sm ' + (dias === n ? 'btn-primary' : 'btn-secondary'), dataset: { atalho: String(n) }, onclick: () => usarAtalho(n) }, r))),
      el('span', { style: { flex: '1' } }),
      // a dupla PDF · Planilha, do mesmo jeito que aparece na tela de
      // Hóspedes do Monteiro de Moraes
      trilha && trilha.length && temPdf()
        ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'ad-pdf', onclick: baixarPdf }, icone('ic-baixar'), 'PDF')
        : null,
      trilha && trilha.length && temPlanilha()
        ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'ad-planilha', onclick: baixarPlanilha }, icone('ic-baixar'), 'Planilha')
        : null);
  }

  // A cor do grupo vira BADGE, que é o componente que o app do Monteiro
  // de Moraes usa para classificar linha de tabela. Cinco badges, nenhum
  // inventado: são os mesmos .badge-* do index.html.
  const BADGE = { reserva: 'badge-blue', hospede: 'badge-purple', checkin: 'badge-green',
                  limpeza: 'badge-orange', precheckin: 'badge-orange', unidade: 'badge-gray' };

  function linha(e) {
    const q = quemDoEvento(e);
    const det = detalheDoEvento(e);
    const g = grupoDaAcao(e.acao);
    return el('tr', { dataset: { acao: e.acao, grupo: g } },
      el('td', { 'data-label': 'Hora', dataset: { hora: '1' } }, horaDe(e.em, fuso)),
      el('td', { 'data-label': 'O que aconteceu', class: 'td-full' },
        el('span', { class: 'badge ' + (BADGE[g] || 'badge-gray'), dataset: { marca: g }, style: { marginRight: '8px' } }, rotuloDaAcao(e.acao)),
        det ? el('span', { dataset: { detalhe: '1' } }, det) : null),
      el('td', { 'data-label': 'Quem', dataset: { quem: '1' }, style: { fontStyle: q.sistema ? 'italic' : 'normal', color: '#6b7280' } }, q.nome));
  }

  (async () => {
    try { await carregarUnidades(); } catch (e) { if (vivo) cont.replaceChildren(el('div', { class: 'alert alert-error' }, 'Não foi possível carregar os apartamentos: ', e.message)); return; }
    if (!vivo) return;
    if (aba === 'indicadores') buscarIndicadores(); else desenhar();
  })();

  return () => { vivo = false; destruirGraficos(); };
}
